import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { http, formatApiError } from "../lib/api";
import { applyRoleSets } from "../lib/roles";
import { clearSessionData } from "../lib/storage";
import { tabSessionAlive, listenTabPings, markTabAlive } from "../lib/tabSession";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = not-auth, obj = auth
  const [error, setError] = useState("");
  const [roleSetsVersion, setRoleSetsVersion] = useState(0);

  // Rol Yetki Matrisi'nden düzenlenen rol listeleri (menü / sayfa görünürlüğü) — oturum kurulunca sunucudan alınır
  const syncRoleSets = useCallback(() => { http.get("/roles/sets").then((r) => { applyRoleSets(r.data); setRoleSetsVersion((v) => v + 1); }).catch(() => {}); }, []);
  useEffect(() => { if (user) syncRoleSets(); }, [user, syncRoleSets]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await http.get("/auth/me");
      setUser(data);
    } catch (e) {
      // yönetici oturumu kapattıysa / süresi dolduysa giriş ekranında neden gösterilir (ilk ziyaretteki 'Kimlik doğrulanmadı' için not yok)
      const d = String(e?.response?.data?.detail || "");
      try { if (/kapatıldı/i.test(d)) localStorage.setItem("pas_logout_reason", "closed"); else if (/doldu/i.test(d)) localStorage.setItem("pas_logout_reason", "expired"); } catch { /* özel mod */ }
      setUser(false);
    }
  }, []);

  // Sekme kapanınca otomatik çıkış: sekme işareti yoksa ve başka açık sekme de yanıt vermiyorsa sunucu oturumu kapatılır
  const dropStaleSession = useCallback(async () => {
    clearSessionData();
    try { await http.post("/auth/logout"); } catch (e) { console.info("[auth] eski oturum kapatılamadı (zaten yok olabilir)", e?.response?.status); }
    markTabAlive();
    setUser(false);
  }, []);

  useEffect(() => {
    const stopPings = listenTabPings();
    // Google girişinden dönüş (#session_id=…): /auth/me çağrılmaz, oturumu AuthCallback kurar
    if (!window.location.hash?.includes("session_id=")) {
      tabSessionAlive().then((alive) => (alive ? refresh() : dropStaleSession()));
    }
    return stopPings;
  }, [refresh, dropStaleSession]);

  // Sunucu oturumu kapandı (yönetici kapattı / süresi doldu): yerel oturumu düşür, giriş ekranında neden göster
  useEffect(() => {
    const onExpired = (e) => {
      setUser((u) => {
        if (!u) return u;
        try { localStorage.setItem("pas_logout_reason", /kapatıldı/i.test(e.detail || "") ? "closed" : "expired"); } catch { /* özel mod */ }
        clearSessionData();
        return false;
      });
    };
    window.addEventListener("auth-expired", onExpired);
    return () => window.removeEventListener("auth-expired", onExpired);
  }, []);

  const login = useCallback(async (email, password) => {
    setError("");
    try {
      const { data } = await http.post("/auth/login", { email, password });
      markTabAlive();
      setUser(data);
      return data;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  }, []);

  // Emergent Google girişi — REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = useCallback(() => {
    setError("");
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  // Google dönüşündeki session_id → backend oturumu (yalnız tanımlı kullanıcılar; hata → giriş ekranında gösterilir)
  const googleSession = useCallback(async (sessionId) => {
    try {
      const { data } = await http.post("/auth/google/session", { session_id: sessionId });
      markTabAlive();
      setUser(data);
      return data;
    } catch (e) {
      setError(formatApiError(e));
      setUser(false);
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    clearSessionData();
    try { await http.post("/auth/logout"); } catch (e) { console.warn("[auth] çıkış isteği başarısız; yerel oturum yine de kapatıldı", e); }
    setUser(false);
  }, []);

  const value = useMemo(() => ({ user, error, login, loginWithGoogle, googleSession, logout, refresh, syncRoleSets, roleSetsVersion, isAdmin: user && user.role === "admin" }), [user, error, login, loginWithGoogle, googleSession, logout, refresh, syncRoleSets, roleSetsVersion]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
